<template>
    <div class="custom-dropdown relative" :class="{ 'is-open': isOpen }" ref="dropdown">
        <button
            type="button"
            class="dropdown-trigger"
            :class="{ 'is-active': isOpen }"
            @click="toggleDropdown"
            @keydown.enter.prevent="toggleDropdown"
            @keydown.space.prevent="toggleDropdown"
            @keydown.escape="closeDropdown"
            @keydown.down.prevent="openDropdown"
            :aria-expanded="isOpen"
            :aria-haspopup="true"
            ref="trigger"
        >
            <span class="dropdown-trigger-content">
                <template v-if="selectedOption">
                    <slot
                        name="trigger"
                        :option="selectedOption"
                        :label="getOptionLabel(selectedOption)"
                    >
                        <span class="dropdown-label">{{ getOptionLabel(selectedOption) }}</span>
                    </slot>
                </template>
                <template v-else>
                    <slot name="placeholder">
                        <span class="dropdown-placeholder">{{ placeholder }}</span>
                    </slot>
                </template>
            </span>
            <span class="far fa-chevron-down"></span>
        </button>
        <transition name="dropdown-fade">
            <div
                v-if="isOpen"
                class="dropdown-menu"
                @click.stop
                @keydown="handleKeyDown"
                tabindex="-1"
                ref="menu"
            >
                <div
                    v-for="(option, index) in options"
                    :key="getOptionKey(option, index)"
                    class="dropdown-item"
                    :class="{
                        'is-selected': isOptionSelected(option, index),
                        'is-highlighted': highlightedIndex === index
                    }"
                    @click="selectOption(option, index)"
                    @mouseenter="highlightedIndex = index"
                    :ref="`item-${index}`"
                >
                    <slot
                        name="option"
                        :option="option"
                        :index="index"
                        :label="getOptionLabel(option)"
                        :selected="isOptionSelected(option, index)"
                        :highlighted="highlightedIndex === index"
                    >
                        <Icon
                            v-if="option.icon"
                            :icon-class="option.icon"
                            :size="option.iconSize || 3"
                            class="dropdown-icon"
                        />
                        <span class="dropdown-item-label">{{ getOptionLabel(option) }}</span>
                        <Icon
                            v-if="isOptionSelected(option, index)"
                            icon-class="ui-check"
                            size="3"
                            class="dropdown-check"
                        />
                    </slot>
                </div>
                <div v-if="options.length === 0" class="dropdown-empty">
                    {{ emptyText }}
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import Icon from "../icon/icon.vue";

export default {
    name: "Dropdown",
    components: {
        Icon
    },
    props: {
        /*
        format: {
        *   label: string,
        *   value: string,
        *   icon: string,
        *   iconSize: number,
        * }
        */
        options: {
            type: Array,
            required: true,
            default: () => []
        },
        value: {
            type: [String, Number, Object],
            default: null
        },
        placeholder: {
            type: String,
            default: "Selecione uma opção"
        },
        emptyText: {
            type: String,
            default: "Nenhuma opção disponível"
        },
        optionValue: {
            type: String,
            default: "value"
        },
        optionLabel: {
            type: String,
            default: "label"
        },
        optionKey: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isOpen: false,
            highlightedIndex: -1
        };
    },
    computed: {
        selectedOption() {
            if (!this.value) return null;
            return this.findOptionByValue(this.value);
        }
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    watch: {
        isOpen(newValue) {
            if (newValue) {
                this.$nextTick(() => {
                    this.scrollToSelected();
                    this.highlightedIndex = this.getSelectedIndex();
                    if (this.highlightedIndex < 0 && this.options.length > 0) {
                        this.highlightedIndex = 0;
                    }
                    this.$refs.menu?.focus();
                });
            } else {
                this.highlightedIndex = -1;
                this.$refs.trigger?.focus();
            }
        }
    },
    methods: {
        toggleDropdown(event) {
            event.stopPropagation();
            this.isOpen = !this.isOpen;
        },
        openDropdown() {
            if (!this.isOpen) {
                this.isOpen = true;
            }
        },
        closeDropdown() {
            this.isOpen = false;
        },
        selectOption(option, index) {
            const value = this.getOptionValue(option);
            this.$emit("input", value);
            this.$emit("change", value, option, index);
            this.closeDropdown();
        },
        isOptionSelected(option, index) {
            const optionValue = this.getOptionValue(option);
            return this.value === optionValue || 
                   (typeof optionValue === "object" && 
                    typeof this.value === "object" &&
                    JSON.stringify(optionValue) === JSON.stringify(this.value));
        },
        getOptionValue(option) {
            if (typeof option === "string" || typeof option === "number") {
                return option;
            }
            return option[this.optionValue] ?? option;
        },
        getOptionLabel(option) {
            if (typeof option === "string" || typeof option === "number") {
                return String(option);
            }
            return option[this.optionLabel] ?? String(option);
        },
        getOptionKey(option, index) {
            if (this.optionKey && typeof option === "object") {
                return option[this.optionKey];
            }
            const value = this.getOptionValue(option);
            return typeof value === "object" ? index : value;
        },
        findOptionByValue(value) {
            return this.options.find(
                (option, index) => {
                    const optionValue = this.getOptionValue(option);
                    return optionValue === value ||
                           (typeof optionValue === "object" &&
                            typeof value === "object" &&
                            JSON.stringify(optionValue) === JSON.stringify(value));
                }
            );
        },
        getSelectedIndex() {
            if (!this.value) return -1;
            return this.options.findIndex((option, index) => 
                this.isOptionSelected(option, index)
            );
        },
        scrollToSelected() {
            const selectedIndex = this.getSelectedIndex();
            if (selectedIndex >= 0) {
                this.$nextTick(() => {
                    const itemRef = this.$refs[`item-${selectedIndex}`];
                    if (itemRef && itemRef[0]) {
                        itemRef[0].scrollIntoView({ block: "nearest" });
                    }
                });
            }
        },
        handleKeyDown(event) {
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    this.highlightNext();
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    this.highlightPrevious();
                    break;
                case "Enter":
                    event.preventDefault();
                    if (this.highlightedIndex >= 0) {
                        this.selectOption(
                            this.options[this.highlightedIndex],
                            this.highlightedIndex
                        );
                    }
                    break;
                case "Escape":
                    event.preventDefault();
                    this.closeDropdown();
                    break;
                case "Home":
                    event.preventDefault();
                    this.highlightedIndex = 0;
                    this.scrollToHighlighted();
                    break;
                case "End":
                    event.preventDefault();
                    this.highlightedIndex = this.options.length - 1;
                    this.scrollToHighlighted();
                    break;
            }
        },
        highlightNext() {
            if (this.options.length === 0) return;
            this.highlightedIndex =
                this.highlightedIndex < this.options.length - 1
                    ? this.highlightedIndex + 1
                    : 0;
            this.scrollToHighlighted();
        },
        highlightPrevious() {
            if (this.options.length === 0) return;
            this.highlightedIndex =
                this.highlightedIndex > 0
                    ? this.highlightedIndex - 1
                    : this.options.length - 1;
            this.scrollToHighlighted();
        },
        scrollToHighlighted() {
            if (this.highlightedIndex >= 0) {
                this.$nextTick(() => {
                    const itemRef = this.$refs[`item-${this.highlightedIndex}`];
                    if (itemRef && itemRef[0]) {
                        itemRef[0].scrollIntoView({ block: "nearest" });
                    }
                });
            }
        },
        handleClickOutside(event) {
            if (
                this.isOpen &&
                this.$refs.dropdown &&
                !this.$refs.dropdown.contains(event.target) &&
                event.target !== this.$refs.trigger
            ) {
                this.closeDropdown();
            }
        }
    }
};
</script>

<style lang="less" scoped>
.custom-dropdown {
    position: relative;
    display: inline-block;
    width: 100%;

    &.is-open {
        z-index: 1000;
    }
}

.dropdown-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    text-align: left;
    min-height: 38px;

    &:hover {
        border-color: #999;
    }

    &:focus {
        outline: none;
        border-color: var(--primary, #00542E);
        box-shadow: 0 0 0 2px rgba(0, 84, 46, 0.1);
    }

    &.is-active {
        border-color: var(--primary, #00542E);
        box-shadow: 0 0 0 2px rgba(0, 84, 46, 0.1);
    }
}

.dropdown-trigger-content {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
}

.dropdown-placeholder {
    color: #999;
}

.dropdown-label {
    flex: 1;
    min-width: 0;
}

.dropdown-icon {
    margin-right: 8px;
    flex-shrink: 0;
}

.dropdown-chevron {
    margin-left: 8px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
    color: #666;

    &.is-rotated {
        transform: rotate(180deg);
    }
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1001;
    outline: none;
    display: block;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
            background: #555;
        }
    }
}

.dropdown-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    position: relative;

    &:hover,
    &.is-highlighted {
        background-color: #f5f5f5;
    }

    &.is-selected {
        background-color: rgba(0, 84, 46, 0.05);
        color: var(--primary, #00542E);
    }

    &:first-child {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
    }

    &:last-child {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
    }
}

.dropdown-item-label {
    flex: 1;
    min-width: 0;
}

.dropdown-check {
    margin-left: 8px;
    flex-shrink: 0;
    color: var(--primary, #00542E);
}

.dropdown-empty {
    padding: 16px 12px;
    text-align: center;
    color: #999;
    font-size: 14px;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
